import { ConnectError } from "@connectrpc/connect";

import { Transport } from "@/gen/nodebase/v1/worker_pb";

// Formatting helpers shared by the panels. All of them accept the shapes
// protobuf-es actually produces (bigint timestamps, numeric enums) so no caller
// has to remember the conversion.

// errorText surfaces the server's own words. The control plane writes its
// messages for humans and attaches a hint saying what to do next (domain.Error),
// so paraphrasing them in the UI would only lose information — and would mean
// the console and the CLI describe the same refusal differently.
export function errorText(err: unknown): string {
  if (err == null) return "";
  const ce = ConnectError.from(err);
  return ce.rawMessage || ce.message;
}

export function errorReason(err: unknown): string {
  if (err == null) return "";
  return ConnectError.from(err).metadata.get("Nodebase-Reason") ?? "";
}

export function isRegistrationRequired(err: unknown): boolean {
  return errorReason(err) === "registration-required";
}

// enumLabel turns a generated enum member into the word the CLI and the server
// logs use. The generated TS enums are numeric, so the reverse mapping has to
// come from the enum object itself: WorkerState[3] -> "READY" -> "ready".
export function enumLabel(enumObj: object, value: number | undefined): string {
  const name = (enumObj as Record<number, string>)[value ?? 0];
  if (!name || name === "UNSPECIFIED") return "unknown";
  return name.toLowerCase();
}

export function ago(unix: bigint | number | undefined): string {
  const t = Number(unix ?? 0);
  if (!t) return "—";
  const d = Math.floor(Date.now() / 1000 - t);
  if (d < 0) return "now";
  if (d < 90) return `${d}s ago`;
  if (d < 90 * 60) return `${Math.floor(d / 60)}m ago`;
  if (d < 48 * 3600) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export function fmtBytes(n: bigint | number | undefined): string {
  let v = Number(n ?? 0);
  if (!v) return "—";
  for (const unit of ["B", "KB", "MB", "GB"]) {
    if (v < 1024) return `${v < 10 && unit !== "B" ? v.toFixed(1) : Math.round(v)} ${unit}`;
    v /= 1024;
  }
  return `${v.toFixed(1)} TB`;
}

// fmtBitrate renders a bytes-per-second figure as a bitrate — the unit
// operators compare against link capacity. Negative means "no sample yet".
export function fmtBitrate(bytesPerSec: bigint | number | undefined): string {
  const v = Number(bytesPerSec ?? -1);
  if (v < 0) return "—";
  let bits = v * 8;
  for (const unit of ["bps", "kbps", "Mbps"]) {
    if (bits < 1000) return `${bits < 10 && unit !== "bps" ? bits.toFixed(1) : Math.round(bits)} ${unit}`;
    bits /= 1000;
  }
  return `${bits.toFixed(1)} Gbps`;
}

// fmtDuration renders an accumulated seconds figure (endpoint occupancy time)
// at the coarseness the number deserves — nobody compares endpoint uptime by
// the second.
export function fmtDuration(seconds: bigint | number | undefined): string {
  const s = Number(seconds ?? 0);
  if (!s) return "—";
  if (s < 90) return `${Math.round(s)}s`;
  if (s < 90 * 60) return `${Math.round(s / 60)}m`;
  if (s < 48 * 3600) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
}

export function fmtDate(unix: bigint | number | undefined): string {
  const t = Number(unix ?? 0);
  return t ? new Date(t * 1000).toISOString().slice(0, 10) : "—";
}

export function fmtDateTime(unix: bigint | number | undefined): string {
  const t = Number(unix ?? 0);
  return t ? new Date(t * 1000).toLocaleString() : "—";
}

// transportLabel names a device link's WG carrier explicitly so the fleet view
// distinguishes normal WireGuard UDP from the authenticated shared carriers.
// Empty means no device report. Retired WG_TCP reports intentionally stay
// unlabeled instead of surfacing a legacy transport in the operator view.
export function transportLabel(t: Transport | undefined): string {
  switch (t) {
    case Transport.WG_UDP:
      return "WG UDP";
    case Transport.WG_TLS:
      return "WG TLS";
    case Transport.WG_QUIC:
      return "WG QUIC";
    default:
      return "";
  }
}

// tunnelLive says whether a handshake timestamp proves a live tunnel. With 25s
// keepalives a live WG path re-handshakes at least every ~2 minutes; 180s
// covers one missed exchange without calling a dead path live (the client's
// own dead-path rotation draws the line at 150s of silence).
export function tunnelLive(handshakeUnix: bigint | number | undefined): boolean {
  const t = Number(handshakeUnix ?? 0);
  return t > 0 && Date.now() / 1000 - t < 180;
}

// stateTone maps a state word to a badge variant. Only "ready"/"active" are
// good news; the transient states are amber rather than red because they are
// expected, and a red badge for "provisioning" trains people to ignore red.
export function stateTone(
  label: string,
): "success" | "warning" | "destructive" | "secondary" {
  switch (label) {
    case "ready":
    case "active":
      return "success";
    case "provisioning":
    case "setting up":
    case "registering":
    case "rebuilding":
    case "pending":
      return "warning";
    case "degraded":
    case "setup stopped":
    case "suspended":
    case "destroyed":
    case "revoked":
      return "destructive";
    default:
      return "secondary";
  }
}
